// redux/reducers/invoiceReducer.js
const initialState = {
    invoices: [],
    currentInvoice: null,
    loading: false
};

// Load from localStorage
const loadInvoicesFromStorage = () => {
    const stored = localStorage.getItem('hpos_invoices');
    if (stored) {
        try {
            return JSON.parse(stored);
        } catch (e) {
            return [];
        }
    }
    return [];
};

// Save to localStorage
const saveInvoicesToStorage = (invoices) => {
    localStorage.setItem('hpos_invoices', JSON.stringify(invoices));
};

// Generate unique ID
const generateId = () => {
    return `INV_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Generate invoice number
const generateInvoiceNumber = () => {
    const year = new Date().getFullYear();
    const month = String(new Date().getMonth() + 1).padStart(2, '0');
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `INV-${year}${month}-${random}`;
};

export const INVOICE_STATUS = {
    DRAFT: "DRAFT",
    SENT: "SENT",
    PAID: "PAID",
    PARTIAL: "PARTIAL",
    OVERDUE: "OVERDUE",
    CANCELLED: "CANCELLED"
};

export const invoiceReducer = (state = { ...initialState, invoices: loadInvoicesFromStorage() }, action) => {
    let newInvoices;

    switch (action.type) {
        case "INVOICES_LOADING":
            return { ...state, loading: action.payload };

        case "GET_INVOICES":
            return { ...state, invoices: loadInvoicesFromStorage(), loading: false };

        case "SET_INVOICES":
            // Replace current page in memory only — do not crawl/store every invoice in localStorage.
            return {
                ...state,
                invoices: Array.isArray(action.payload) ? action.payload : [],
                loading: false,
            };

        case "UPSERT_INVOICE": {
            const incoming = action.payload;
            if (!incoming) return state;
            const incomingId = String(incoming.POID || incoming.id || "");
            if (!incomingId || incomingId === "null") return state;
            const exists = (state.invoices || []).some(
                (i) => String(i.POID || i.id) === incomingId
            );
            const invoices = exists
                ? (state.invoices || []).map((i) =>
                    String(i.POID || i.id) === incomingId ? { ...i, ...incoming } : i
                )
                : [incoming, ...(state.invoices || [])];
            return { ...state, invoices, loading: false };
        }

        case "GET_INVOICE": {
            const invoice = loadInvoicesFromStorage().find(i => (i.POID || i.id) === action.payload);
            return { ...state, currentInvoice: invoice, loading: false };
        }

        case "CREATE_INVOICE": {
            const newInvoice = {
                ...action.payload,
                POID: generateId(),
                id: generateId(),
                invoiceNumber: generateInvoiceNumber(),
                status: INVOICE_STATUS.DRAFT,
                paidAmount: 0,
                dueAmount: action.payload.total || 0,
                payments: [],
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };
            newInvoices = [newInvoice, ...loadInvoicesFromStorage()];
            saveInvoicesToStorage(newInvoices);
            return { ...state, invoices: newInvoices, currentInvoice: newInvoice, loading: false };
        }

        case "UPDATE_INVOICE": {
            const existingInvoices = loadInvoicesFromStorage();
            newInvoices = existingInvoices.map(i =>
                (i.POID === action.payload.id || i.id === action.payload.id)
                    ? { ...i, ...action.payload.data, updatedAt: new Date().toISOString() }
                    : i
            );
            saveInvoicesToStorage(newInvoices);
            return { ...state, invoices: newInvoices, loading: false };
        }

        case "RECORD_PAYMENT": {
            const existingInvoices = loadInvoicesFromStorage();
            newInvoices = existingInvoices.map(i => {
                if (i.POID === action.payload.id || i.id === action.payload.id) {
                    const newPaidAmount = (i.paidAmount || 0) + action.payload.amount;
                    const newDueAmount = i.total - newPaidAmount;
                    let newStatus = i.status;

                    if (newDueAmount <= 0) {
                        newStatus = INVOICE_STATUS.PAID;
                    } else if (newPaidAmount > 0) {
                        newStatus = INVOICE_STATUS.PARTIAL;
                    }

                    return {
                        ...i,
                        paidAmount: newPaidAmount,
                        dueAmount: newDueAmount,
                        status: newStatus,
                        payments: [...(i.payments || []), {
                            id: generateId(),
                            amount: action.payload.amount,
                            method: action.payload.method,
                            reference: action.payload.reference,
                            date: new Date().toISOString(),
                            notes: action.payload.notes
                        }],
                        paidAt: newDueAmount <= 0 ? new Date().toISOString() : i.paidAt,
                        updatedAt: new Date().toISOString()
                    };
                }
                return i;
            });
            saveInvoicesToStorage(newInvoices);
            return { ...state, invoices: newInvoices, loading: false };
        }

        case "DELETE_INVOICE": {
            const existingInvoices = loadInvoicesFromStorage();
            newInvoices = existingInvoices.filter(i => (i.POID !== action.payload && i.id !== action.payload));
            saveInvoicesToStorage(newInvoices);
            return { ...state, invoices: newInvoices, loading: false };
        }

        default:
            return state;
    }
};